// chat/actions.ts — the chat control executor (cloned from Oriora Content's src/chat/actions.ts).
// The client router (chat/brain.ts) maps ONE user message → ONE ChatAction; this file EXECUTES that
// action against the on-device store (src/store.ts). Everything here is LOCAL SQLite over Tauri IPC —
// no network, no billing. The two AI-billed steps (parse a receipt, parse a query) happen in the
// router via the EXISTING live endpoints, never here.
//
// HITL discipline (mirrors Content): the destructive action (delete_expense) is NEVER executed from a
// chat turn on its own — the Chat UI holds it and requires an explicit Confirm tap first. This
// executor just performs the delete once the UI hands it back.

import { store, applyFilter, type ExpensePatch, type ExpenseFilter } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, BrainError } from "../brain";
import { addInstruction } from "./brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact expense row rendered inline in a chat bubble (filter results).
export interface FilterRow {
  id: string;
  date_on: string;
  vendor: string;
  category: string;
  amount_cents: number;
  currency: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_expense"; vendor: string; amount_cents: number; date_on: string; currency?: string | null; category?: string | null; notes?: string | null }
  | { kind: "add_category"; name: string; color?: string }
  | { kind: "edit_expense"; id: string; patch: ExpensePatch; label?: string }
  | { kind: "delete_expense"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "filter"; filter: ExpenseFilter; label?: string }
  | { kind: "summarize"; month?: string }
  | { kind: "rename_category"; from: string; to: string }
  | { kind: "delete_category"; name: string }
  | { kind: "recategorize"; to_category: string; vendor_contains?: string; from_category?: string }
  | { kind: "set_default_currency"; currency: string }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before this reaches executeAction (replaces all data)
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  | { kind: "add_recurring"; vendor: string; amount_cents: number; frequency: "monthly" | "weekly"; day: number; category?: string | null }
  | { kind: "list_recurring" }
  | { kind: "delete_recurring"; vendor: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;   // shown back in the chat
  changed?: boolean; // the ledger/categories changed → the main list should refresh
  rows?: FilterRow[]; // filter matches to render inline
}

const money = (c: number, currency = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency, currencyDisplay: "narrowSymbol" }).format(c / 100);

function monthStartISO(month?: string): string {
  // Accept "YYYY-MM" / "YYYY-MM-DD"; otherwise the current month.
  if (month && /^\d{4}-\d{2}/.test(month)) return month.slice(0, 7) + "-01";
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_expense": {
        // Resolve the AI-suggested category NAME → the local category id (same as AddExpense does).
        let category_id: string | null = null;
        if (a.category) {
          const cats = await store.listCategories();
          const m = cats.find((c) => c.name.toLowerCase() === a.category!.toLowerCase());
          if (m) category_id = m.id;
        }
        const e = await store.createExpense({
          vendor: a.vendor,
          amount_cents: a.amount_cents,
          date_on: a.date_on,
          // "14.54 euros" must land as EUR, not the default currency — the model names the
          // currency, the row keeps it (real-use QA 2026-08-27: €14.54 was booked as $14.54).
          currency: a.currency && /^[A-Z]{3}$/.test(a.currency) ? a.currency : undefined,
          category_id,
          notes: a.notes ?? null,
        });
        const cat = category_id ? ` · ${a.category}` : "";
        return { ok: true, message: `Added ${e.vendor} — ${money(e.amount_cents, e.currency)} on ${a.date_on}${cat}.`, changed: true };
      }

      case "add_category": {
        const color = a.color && /^#[0-9a-fA-F]{6}$/.test(a.color) ? a.color : "#6B7280";
        const c = await store.createCategory(a.name, color);
        return { ok: true, message: `Added the "${c.name}" category.`, changed: true };
      }

      case "edit_expense":
        await store.updateExpense(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the expense"}.`, changed: true };

      case "delete_expense":
        await store.deleteExpense(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the expense"}.`, changed: true };

      case "filter": {
        const all = await store.listExpenses({ limit: 50000 });
        const rows = applyFilter(all, a.filter);
        const total = rows.reduce((s, e) => s + e.amount_cents, 0);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        const rendered: FilterRow[] = rows.slice(0, 50).map((e) => ({
          id: e.id,
          date_on: e.date_on,
          vendor: e.vendor,
          category: e.category_name ?? "—",
          amount_cents: e.amount_cents,
          currency: e.currency,
        }));
        return {
          ok: true,
          message: `${rows.length} match${rows.length !== 1 ? "es" : ""}${forLabel} · ${money(total)}` + (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rendered,
        };
      }

      case "summarize": {
        const s = await store.dashboardSummary(monthStartISO(a.month));
        const top = s.by_category
          .slice()
          .sort((x, y) => y.total_cents - x.total_cents)
          .slice(0, 3)
          .map((c) => `${c.category_name ?? "Uncategorized"} ${money(c.total_cents)}`)
          .join(" · ");
        const head = `This month: ${money(s.month_total_cents)} across ${s.month_count} expense${s.month_count !== 1 ? "s" : ""}.`;
        return { ok: true, message: top ? `${head}\nTop: ${top}` : head };
      }

      case "rename_category": {
        // No rename in Rust — composite: create the new one, move every expense, drop the old.
        const cats = await store.listCategories();
        const from = cats.find((c) => c.name.toLowerCase() === a.from.toLowerCase());
        if (!from) return { ok: false, message: `There's no category called "${a.from}".` };
        const existing = cats.find((c) => c.name.toLowerCase() === a.to.toLowerCase());
        const target = existing ?? (await store.createCategory(a.to, from.color));
        const rows = (await store.listExpenses({ limit: 50000 })).filter((e) => e.category_id === from.id);
        for (const e of rows) await store.updateExpense(e.id, { category_id: target.id });
        await store.deleteCategory(from.id);
        return { ok: true, message: `Renamed "${from.name}" to "${target.name}" — ${rows.length} expense${rows.length !== 1 ? "s" : ""} moved.`, changed: true };
      }

      case "delete_category": {
        const cat = (await store.listCategories()).find((c) => c.name.toLowerCase() === a.name.toLowerCase());
        if (!cat) return { ok: false, message: `There's no category called "${a.name}".` };
        await store.deleteCategory(cat.id);
        return { ok: true, message: `Deleted the "${cat.name}" category — its expenses are now uncategorized.`, changed: true };
      }

      case "recategorize": {
        const cats = await store.listCategories();
        const target = cats.find((c) => c.name.toLowerCase() === a.to_category.toLowerCase())
          ?? (await store.createCategory(a.to_category, "#6B7280"));
        let rows = await store.listExpenses({ limit: 50000 });
        if (a.vendor_contains) { const t = a.vendor_contains.toLowerCase(); rows = rows.filter((e) => e.vendor.toLowerCase().includes(t)); }
        if (a.from_category) { const f = cats.find((c) => c.name.toLowerCase() === a.from_category!.toLowerCase()); rows = f ? rows.filter((e) => e.category_id === f.id) : []; }
        if (!a.vendor_contains && !a.from_category) return { ok: false, message: "Tell me which ones to move — by name (e.g. Starbucks) or from which category." };
        for (const e of rows) await store.updateExpense(e.id, { category_id: target.id });
        return { ok: true, message: `Moved ${rows.length} expense${rows.length !== 1 ? "s" : ""} to "${target.name}".`, changed: rows.length > 0 };
      }

      case "set_default_currency": {
        try { localStorage.setItem("oriora.expense.currency", a.currency); window.dispatchEvent(new Event("oriora:currency-changed")); } catch { /* ignore */ }
        return { ok: true, message: `Default currency is now ${a.currency} — new expenses use it.`, changed: true };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // Photo-heavy backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup is photo-heavy, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path (receipt photos add up). One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: `Backed up your whole library to your ${name} — restore it on any device, any time.` };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size, receipt photos included.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large photo-heavy backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my library" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large (it can be, with receipt photos), use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : "No cloud backup found yet — say \"back up my library\" first." };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored your library from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_to_cloud": {
        // Saves the CURRENT conversation (or a note) to the customer's OWN cloud — distinct from
        // backup_save (the whole expense library). Text relay only; Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "add_recurring": {
        let category_id: string | null = null;
        if (a.category) {
          const m = (await store.listCategories()).find((c) => c.name.toLowerCase() === a.category!.toLowerCase());
          if (m) category_id = m.id;
        }
        const r = await store.createRecurring({ vendor: a.vendor, amount_cents: a.amount_cents, frequency: a.frequency, day: a.day, category_id });
        const when = r.frequency === "weekly"
          ? `every week on ${["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][r.day] ?? "day " + r.day}`
          : `every month on day ${r.day}`;
        const added = await store.materializeRecurring(new Date().toISOString().slice(0, 10));
        return { ok: true, message: `Recurring set: ${r.vendor} — ${money(r.amount_cents, r.currency)} ${when}.${added > 0 ? ` Added ${added} due entr${added === 1 ? "y" : "ies"} now.` : ""}`, changed: added > 0 };
      }

      case "list_recurring": {
        const rules = await store.listRecurring();
        if (!rules.length) return { ok: true, message: 'No recurring expenses yet — say e.g. "rent 1200 every month on the 1st".' };
        const lines = rules.map((r) => `• ${r.vendor} — ${money(r.amount_cents, r.currency)} ${r.frequency === "weekly" ? `weekly (${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][r.day] ?? r.day})` : `monthly (day ${r.day})`}`);
        return { ok: true, message: "Recurring expenses:\n" + lines.join("\n") };
      }

      case "delete_recurring": {
        const rules = await store.listRecurring();
        const t = a.vendor.toLowerCase();
        const hit = rules.find((r) => r.vendor.toLowerCase().includes(t));
        if (!hit) return { ok: false, message: `No recurring expense matching "${a.vendor}".` };
        await store.deleteRecurring(hit.id);
        return { ok: true, message: `Stopped the recurring ${hit.vendor} — already-added entries stay.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: `Noted — from now on: ${a.instruction}` };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
